/**
 * QXAI Agent Service - Frontend SSE Client
 *
 * Communicates with /api/qxai/agent-native/run endpoint
 * Streams run, step, and done events via Server-Sent Events (SSE)
 */

const VTAAS_API_BASE = 'https://vtaas-backend-220419304601.us-central1.run.app';

/**
 * Start an agent run with streaming events
 *
 * @param {string} goal - The user's goal/request
 * @param {object} callbacks - Event handlers
 * @param {Function} callbacks.onStep - Called on each step event
 * @param {Function} callbacks.onRun - Called on run event
 * @param {Function} callbacks.onDone - Called on done event
 * @param {Function} callbacks.onError - Called on error
 * @returns {Function} - Abort function to cancel the request
 */
export function approveAgentRun(runId, decision, { onStep, onRun, onDone, onError }) {
  const controller = new AbortController();
  const userHash = localStorage.getItem('qxai_user_hash');
  fetch(`${VTAAS_API_BASE}/api/qxai/agent-native/runs/${runId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-User-Hash': userHash },
    body: JSON.stringify({ decision }),
    signal: controller.signal,
  }).then(async (res) => {
    if (!res.ok || !res.body) { onError?.(new Error('approve failed')); return; }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const chunks = buf.split('\n\n');
      buf = chunks.pop();
      for (const chunk of chunks) {
        const ev = chunk.match(/event: (\w+)/);
        const data = chunk.match(/data: (.*)/s);
        if (!ev || !data) continue;
        const payload = JSON.parse(data[1]);
        if (ev[1] === 'run') onRun?.(payload);
        else if (ev[1] === 'step') onStep?.(payload);
        else if (ev[1] === 'done') onDone?.(payload);
      }
    }
  }).catch((e) => { if (e.name !== 'AbortError') onError?.(e); });
  return () => controller.abort();
}

export function startAgentRun(goal, { onStep, onRun, onDone, onError }) {
  const controller = new AbortController();
  const userHash = localStorage.getItem('qxai_user_hash');

  fetch(`${VTAAS_API_BASE}/api/qxai/agent-native/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-Hash': userHash,
    },
    body: JSON.stringify({ goal }),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok || !res.body) {
        onError?.(new Error('agent run failed'));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split('\n\n');
        buf = chunks.pop();

        for (const chunk of chunks) {
          const evMatch = chunk.match(/event: (\w+)/);
          const dataMatch = chunk.match(/data: (.*)/s);

          if (!evMatch || !dataMatch) continue;

          try {
            const payload = JSON.parse(dataMatch[1]);

            if (evMatch[1] === 'run') onRun?.(payload);
            else if (evMatch[1] === 'step') onStep?.(payload);
            else if (evMatch[1] === 'done') onDone?.(payload);
          } catch (parseError) {
            console.error('Failed to parse SSE event:', parseError);
            onError?.(parseError);
          }
        }
      }
    })
    .catch((e) => {
      if (e.name !== 'AbortError') onError?.(e);
    });

  return () => controller.abort();
}
